import { cx, Link } from '@elsa/ui';
import { type ReactNode, useEffect } from 'react';
import { Sageata, Simbol } from '../brand.tsx';
import { WRAP } from './PublicLayout.tsx';

// Prima pagina, dupa macheta din kitul ELSA (07-Landing): hero negru cu „Vezi ce cumpara
// statul.”, o previzualizare a aplicatiei cu date de exemplu, cele patru module, pentru cine,
// cum incepi si banda rosie de la final. Textele spun ce face Elsa azi (achizitiile directe,
// preturile, fisele, alertele, echipa), iar in locul formularului de demo din macheta (care nu
// trimitea nimic) sunt inregistrarea si intrarea in cont.

const SIGNUP = '/autentificare?mod=inregistrare';

const bigButton =
  'flex min-w-[220px] items-center justify-between gap-8 border-2 px-5 py-[15px] text-base font-bold';

function SectionHead({
  id,
  kicker,
  title,
  dark,
}: {
  id: string;
  kicker: string;
  title: string;
  dark?: boolean;
}) {
  return (
    <>
      <span className={cx('eticheta-mare', dark ? 'text-otel' : 'text-semnal-700')}>{kicker}</span>
      <h2
        id={`${id}-titlu`}
        className={cx(
          'max-w-[980px] text-[clamp(36px,5.2vw,72px)] leading-none font-extrabold tracking-[-0.03em] text-pretty',
          dark ? 'text-hartie' : 'text-cerneala',
        )}
      >
        {title}
        <span className="text-semnal">.</span>
      </h2>
    </>
  );
}

function Section({ id, dark, children }: { id: string; dark?: boolean; children: ReactNode }) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-titlu`}
      className={cx('scroll-mt-4 py-[clamp(56px,9vw,128px)]', dark ? 'bg-cod' : 'bg-hartie')}
    >
      <div className={cx(WRAP, 'flex flex-col gap-10')}>{children}</div>
    </section>
  );
}

const MODULES: { title: string; text: string; points: string[] }[] = [
  {
    title: 'Achiziții',
    text: 'Toate achizițiile directe din SEAP, cu produsele și prețurile lor.',
    points: [
      'Cuvinte, CPV, județ, valoare, perioadă',
      'Căutări salvate și export CSV',
      'Produsele din fiecare achiziție',
    ],
  },
  {
    title: 'Concurență',
    text: 'Cine vinde pe codurile tale, cui și la ce preț.',
    points: [
      'Fișa fiecărui furnizor',
      'Prețul unitar pe fiecare cod CPV',
      'Minim, median și maxim, lună de lună',
    ],
  },
  {
    title: 'Instituții',
    text: 'Ce cumpără fiecare autoritate, de la cine și cu cât.',
    points: [
      'Fișa fiecărei autorități',
      'Codurile CPV și furnizorii principali',
      'Toate achizițiile ei, în istoricul planului',
    ],
  },
  {
    title: 'Echipa de vânzări',
    text: 'Fiecare coleg vede piața din zona lui.',
    points: [
      'Roluri pe care le definești tu',
      'Județele fiecărui agent',
      'Alerte personale sau ale firmei',
    ],
  },
];

const AUDIENCE: [string, string][] = [
  [
    'Directori de vânzări',
    'Piața din achiziții directe, pe codurile firmei și pe județele fiecărui agent, fără rapoarte manuale.',
  ],
  ['Agenți de vânzări', 'Achizițiile noi din zona lor, în fiecare dimineață, pe email.'],
  [
    'Departament de ofertare',
    'Prețuri de referință pe fiecare cod CPV, din produsele cumpărate efectiv de autorități.',
  ],
  ['Manageri și proprietari', 'Cine cumpără ce vindeți, cât, cât de des și de la cine.'],
  [
    'Consultanți în achiziții',
    'Date pe orice cod CPV și orice județ, pentru mai multe firme dintr-un singur cont.',
  ],
];

const STEPS: [string, string][] = [
  ['Contul', 'Te înregistrezi cu emailul de serviciu. 14 zile de probă, fără card.'],
  ['Firma', 'Introduci CUI-ul, datele vin din ANAF. Alegi codurile CPV urmărite.'],
  ['Alerte și echipa', 'Setezi alertele și îi inviți pe colegi. Dimineața primești ce e nou.'],
];

export function LandingPage() {
  // Linkurile „/#module” din alte pagini: sectiunea exista abia dupa randare.
  useEffect(() => {
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, []);
  return (
    <>
      <section className="relative overflow-hidden bg-cod pt-[clamp(56px,9vw,120px)] pb-[clamp(48px,6vw,96px)]">
        <Simbol
          size={900}
          inel="#1f1d1c"
          felie="#2a1410"
          className="pointer-events-none absolute -top-[200px] -right-[260px]"
        />
        <div className={cx(WRAP, 'relative flex flex-col gap-[clamp(40px,6vw,72px)]')}>
          <div className="flex max-w-[900px] flex-col gap-7">
            <span className="eticheta-mare text-semnal">Analiză SEAP pentru firme</span>
            <h1 className="text-[clamp(52px,9vw,128px)] leading-[.92] font-extrabold tracking-[-0.035em] text-hartie">
              Vezi ce cumpără statul<span className="text-semnal">.</span>
            </h1>
            <p className="max-w-[640px] text-[clamp(17px,1.6vw,21px)] leading-[1.55] text-pretty text-otel">
              ELSA extrage zilnic achizițiile directe din SEAP, cu produsele și prețurile lor, și
              îți arată oportunitățile, concurența și autoritățile care cumpără ce vinzi, într-un
              singur loc.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={SIGNUP}
                className={cx(
                  bigButton,
                  'border-semnal bg-semnal text-cod hover:border-semnal-600 hover:bg-semnal-600',
                )}
              >
                <span>Începe perioada de probă</span>
                <Sageata />
              </Link>
              <a
                href="#module"
                className={cx(bigButton, 'border-grafit text-hartie hover:border-hartie')}
              >
                <span>Vezi modulele</span>
                <Sageata />
              </a>
            </div>
            <p className="text-sm text-otel">
              14 zile gratuit, fără card. Ai deja cont?{' '}
              <Link to="/autentificare" className="font-semibold text-hartie underline">
                Intră în cont
              </Link>
            </p>
          </div>
          <figure>
            <AppPreview />
            <figcaption className="mt-3 text-xs text-otel">Machetă cu date de exemplu.</figcaption>
          </figure>
        </div>
      </section>

      <Section id="module">
        <SectionHead id="module" kicker="Module" title="Patru module. O singură imagine a pieței" />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-[2px] border-2 border-cerneala bg-cerneala">
          {MODULES.map((m, i) => (
            <div key={m.title} className="flex flex-col gap-4 bg-hartie p-7">
              <span className="text-[15px] font-bold font-stretch-[118%] text-semnal-700">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="text-[28px] leading-[1.1] font-extrabold tracking-[-0.01em]">
                {m.title}
              </h3>
              <p className="text-base text-pretty text-grafit">{m.text}</p>
              <ul className="mt-auto flex flex-col">
                {m.points.map((p) => (
                  <li key={p} className="border-t-2 border-cerneala py-2.5 text-sm font-semibold">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section id="pentru-cine" dark>
        <SectionHead
          id="pentru-cine"
          kicker="Pentru cine"
          title="Pentru toți cei care vând statului"
          dark
        />
        <div className="flex flex-col">
          {AUDIENCE.map(([who, what]) => (
            <div
              key={who}
              className="flex flex-wrap items-baseline gap-x-10 gap-y-2 border-t-2 border-grafit py-[22px]"
            >
              <h3 className="flex-[1_1_280px] text-[clamp(22px,2.4vw,30px)] font-extrabold text-hartie">
                {who}
              </h3>
              <p className="flex-[2_1_320px] text-[17px] leading-normal text-otel">{what}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="cum-incepi">
        <SectionHead
          id="cum-incepi"
          kicker="Cum începi"
          title="Trei pași până la primele oportunități"
        />
        <ol className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[2px] border-2 border-cerneala bg-cerneala">
          {STEPS.map(([title, text], i) => (
            <li key={title} className="flex flex-col gap-3.5 bg-hartie p-7">
              <span className="text-5xl leading-none font-extrabold font-stretch-[118%] text-semnal">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="text-[26px] font-extrabold">{title}</h3>
              <p className="text-base text-grafit">{text}</p>
            </li>
          ))}
        </ol>
        <p className="text-xs text-gri-700">
          ELSA e doar pentru firme (B2B). Datele provin din surse publice; ELSA nu e afiliată ADR
          sau SEAP.
        </p>
      </Section>

      <section
        id="incepe"
        aria-labelledby="incepe-titlu"
        className="bg-semnal py-[clamp(56px,9vw,128px)]"
      >
        <div
          className={cx(
            WRAP,
            'grid items-start gap-[clamp(40px,6vw,96px)] lg:grid-cols-[1.4fr_1fr]',
          )}
        >
          <div className="flex flex-col gap-7">
            <Simbol size={72} inel="#121110" felie="#f3f2f2" />
            <h2
              id="incepe-titlu"
              className="text-[clamp(40px,6.6vw,88px)] leading-[.92] font-extrabold tracking-[-0.035em] text-cod"
            >
              Oportunitățile, înaintea concurenței.
            </h2>
            <p className="max-w-[480px] text-lg text-cod">
              Îți faci contul în două minute și vezi ELSA pe datele firmei tale.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <ul className="grid gap-[2px] border-2 border-cod bg-cod sm:grid-cols-3">
              {[
                ['14 zile', 'de probă, fără card'],
                ['3 locuri', 'în echipa firmei'],
                ['90 de zile', 'de istoric SEAP'],
              ].map(([n, t]) => (
                <li key={n} className="flex flex-col gap-1 bg-hartie px-4 py-3.5">
                  <span className="text-xl leading-none font-extrabold whitespace-nowrap tabular-nums">
                    {n}
                  </span>
                  <span className="text-sm text-grafit">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to={SIGNUP}
              className="flex items-center justify-between border-2 border-cod bg-cod px-5 py-4 text-base font-bold text-hartie hover:bg-cerneala"
            >
              <span>Creează contul firmei</span>
              <Sageata />
            </Link>
            <Link
              to="/autentificare"
              className="flex items-center justify-between border-2 border-cod px-5 py-4 text-base font-bold text-cod hover:bg-cod/10"
            >
              <span>Am deja cont</span>
              <Sageata />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

// Previzualizarea aplicatiei (date de exemplu): bara laterala verde, cifrele, graficul cu o
// singura bara rosie (saptamana curenta) si tabelul, exact ca in macheta.
const BARS = [42, 55, 38, 61, 47, 70, 52, 66, 58, 74, 63, 88];
const ROWS: [string, string, string, string][] = [
  ['Spitalul Clinic Județean de Urgență', '30213000-5 · Computere personale', '48.900', '28 oct.'],
  ['Consiliul Județean', '48000000-8 · Pachete software', '125.000', '27 oct.'],
  ['Universitatea Tehnică', '30232110-8 · Imprimante laser', '8.690', '27 oct.'],
  ['Primăria Municipiului', '72000000-5 · Servicii IT', '64.000', '24 oct.'],
];
const COLS = 'grid grid-cols-[minmax(200px,1.4fr)_minmax(220px,1.6fr)_110px_70px] gap-3 px-[18px]';

function AppPreview() {
  return (
    <div aria-hidden="true" className="flex flex-wrap border-2 border-grafit bg-gri-300">
      <div className="flex flex-[1_1_200px] flex-col gap-[22px] bg-verde px-4 py-5">
        <div className="flex items-center gap-1.5 px-2">
          <Simbol size={21} mic />
          <span className="text-xl leading-[.8] font-extrabold tracking-[.02em] font-stretch-[118%] text-hartie">
            ELSA
          </span>
        </div>
        <div className="flex flex-col gap-[2px] text-sm">
          {['Acasă', 'Achiziții', 'Produse', 'Prețuri', 'Alerte'].map((n) => (
            <div
              key={n}
              className={cx(
                'flex items-center justify-between px-2 py-2.5',
                n === 'Achiziții' ? 'bg-cod font-bold text-hartie' : 'text-otel',
              )}
            >
              <span>{n}</span>
              {n === 'Alerte' ? (
                <span className="bg-semnal px-1.5 py-0.5 text-[11px] font-extrabold text-cod">
                  12
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className="mt-auto border-t-2 border-grafit px-2 pt-3 text-xs text-otel">
          Firma Exemplu SRL
        </div>
      </div>
      <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-[2px] bg-gri-300">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-hartie p-[18px]">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-[22px] font-extrabold">Achiziții</span>
            <span className="text-[13px] text-grafit">ultimele 30 de zile</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['CPV: codurile firmei', 'Județ: toate', 'Valoare > 5.000 lei'].map((c) => (
              <span
                key={c}
                className="border-2 border-cerneala px-2.5 py-[5px] text-xs font-semibold whitespace-nowrap"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
        <div className="grid gap-[2px] sm:grid-cols-3">
          {[
            ['Achiziții noi', '1.284', ''],
            ['Valoare totală', '12,8', 'mil. lei'],
            ['Potrivite alertelor tale', '37', ''],
          ].map(([label, value, unit], i) => (
            <div key={label} className="flex flex-col gap-2.5 bg-hartie px-[18px] py-4">
              <span className="eticheta text-gri-700">{label}</span>
              <span
                className={cx(
                  'text-[40px] leading-none font-extrabold tracking-[-0.02em] tabular-nums',
                  i === 2 ? 'text-semnal' : 'text-cerneala',
                )}
              >
                {value}
                {unit ? <span className="text-base font-semibold text-grafit"> {unit}</span> : null}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-3.5 bg-hartie px-[18px] py-4">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="eticheta text-gri-700">Valoare pe săptămână</span>
            <span className="text-xs text-grafit">ultimele 12 săptămâni</span>
          </div>
          <div className="flex h-[140px] items-end gap-1.5 border-b-2 border-cerneala">
            {BARS.map((h, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: barele sunt fixe
                key={i}
                className={i === BARS.length - 1 ? 'bg-semnal' : i % 2 ? 'bg-grafit' : 'bg-otel'}
                style={{ flex: 1, height: `${h}%` }}
              />
            ))}
          </div>
        </div>
        <div className="overflow-x-auto bg-hartie">
          <div className="min-w-[640px]">
            <div className={cx(COLS, 'eticheta border-b-2 border-cerneala py-3 text-gri-700')}>
              <span>Autoritate</span>
              <span>Obiect</span>
              <span className="text-right">Valoare, lei</span>
              <span className="text-right">Data</span>
            </div>
            {ROWS.map(([who, what, value, day], i) => (
              <div
                key={who}
                className={cx(
                  COLS,
                  'py-3 text-[13px] tabular-nums',
                  i < ROWS.length - 1 && 'border-b border-gri-300',
                )}
              >
                <span className="font-bold">{who}</span>
                <span className="text-grafit">{what}</span>
                <span className="text-right">{value}</span>
                <span className="text-right">{day}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
